import React, { useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, MenuItem, Select,
} from '@mui/material';

interface RejectedDescriptionDialogProps {
    /** Whether the dialog is visible. */
    open: boolean;
    /** Pre-loaded list of available rejection descriptions sourced from the backend. */
    descriptions: string[];
    /** Whether a save request is in flight. Disables the Save button. */
    loading: boolean;
    /** Called when the user dismisses the dialog without saving. */
    onClose: () => void;
    /**
     * Called when the user confirms a description.
     * The parent is responsible for executing the status-change with the returned description.
     * @param description - The description string selected by the user.
     */
    onSave: (description: string) => void;
}

/**
 * Modal popup shown whenever a record's status is changed to **Rejected**.
 *
 * The user must pick an existing description from the dropdown before saving.
 * Adding new descriptions is handled by the separate {@link QueueUploadDescription}
 * component (Component A) available from the filter bar's action slot.
 *
 * Internal selection state is reset each time the dialog opens so it does not
 * carry over between successive Rejected-status changes.
 *
 * @example
 * <RejectedDescriptionDialog
 *   open={rejectedDialog.open}
 *   descriptions={descriptions}
 *   loading={loading}
 *   onClose={() => setRejectedDialog(prev => ({ ...prev, open: false }))}
 *   onSave={(desc) => { pendingAction(desc); }}
 * />
 */
const RejectedDescriptionDialog: React.FC<RejectedDescriptionDialogProps> = ({
    open,
    descriptions,
    loading,
    onClose,
    onSave,
}) => {
    const [selectedDescription, setSelectedDescription] = useState<string>('');

    const handleSave = () => {
        onSave(selectedDescription);
    };

    // Reset selection when dialog opens
    React.useEffect(() => {
        if (open) setSelectedDescription('');
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Rejection Description</DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel id="rejected-desc-label">Description</InputLabel>
                    <Select
                        labelId="rejected-desc-label"
                        value={selectedDescription}
                        label="Description"
                        onChange={(e) => setSelectedDescription(e.target.value as string)}
                    >
                        {descriptions.map(d => (
                            <MenuItem key={d} value={d}>{d}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={loading || !selectedDescription}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RejectedDescriptionDialog;
